import React, { useRef, useState } from 'react';
import { Formik } from 'formik';
import { useSelector } from 'react-redux';
import Input from 'components/FormFields/FormikInput';
import Button from 'components/Button';
import Modal from 'components/Modal';
import ReviewContent from './ReviewContent';
import { getSchema, getInitialValues } from './utils';
import classes from './SignatureModal.module.scss';

const fonts = [
  '"Cedarville Cursive", serif',
  '"Caveat", serif',
  '"Pecita", serif',
  '"Homemade Apple", serif',
  '"Bad Script", serif',
  '"Freehand", serif',
  '"Bonheur Royale", serif',
  '"Seaweed Script", serif',
  '"Shadows Into Light", serif',
  '"Permanent Marker", serif',
];

const SignatureModal = ({ onSubmit }) => {
  const formRef = useRef(null);
  const currentUser = useSelector(({ auth }) => auth.user);
  const initialValues = getInitialValues(currentUser);
  const [isReview, setIsReview] = useState(false);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const fontStyles = {
    fontFamily: fonts[index],
    fontSize: '30px',
    lineHeight: '36px',
    color: '#1E202B',
  };
  const renderEditForm = formProps => {
    const { values, handleSubmit, setFieldValue } = formProps;

    return (
      <form className={classes.formInner}>
        {isReview ? (
          <ReviewContent name={currentUser?.name} fontStyles={fontStyles} />
        ) : (
          <>
            <div className={classes.names}>
              <Input
                name="full_name"
                label="Full Name"
                size="large"
                placeholder=""
                hideErrorMessage
                textSize="md"
                isDisabled
              />
              <Input
                name="short_name"
                label="Initials"
                size="large"
                placeholder=""
                hideErrorMessage
                textSize="md"
                maxLength={2}
                value={values.short_name}
                onCustomChange={e => {
                  const tValue = e.target.value || '';
                  setFieldValue('short_name', tValue.trim().toUpperCase());
                }}
              />
            </div>
            <p className={classes.description}>
              The signature below will be used for signing documents related to
              your responsibilities as a <b>{currentUser?.role?.name}</b>.
              Wherever a signature is required, you’ll see the option to input
              your digital signature. By clicking the corresponding button, the
              signature below will be saved on your behalf.
            </p>
            <div className={classes.signature}>
              <div>
                <label>Signature</label>
                <div style={fontStyles}>{values.full_name}</div>
              </div>
              <div>
                <label>Initials</label>
                <div style={fontStyles}>{values.short_name}</div>
              </div>
            </div>
            <div>
              <Button
                buttonType="button"
                onClick={() => {
                  let newIndex = index + 1;
                  if (index === fonts.length - 1) {
                    newIndex = 0;
                  }
                  setIndex(newIndex);
                }}
                type="link"
                weight="md"
                className="gx-mb-0 gx-mt-2"
              >
                Regenerate
              </Button>
            </div>
          </>
        )}
        <div className={classes.footerWrapper}>
          <div className={classes.footer}>
            <Button
              buttonType="button"
              onClick={() => {
                handleSubmit(values);
              }}
              size="large"
              type="primary"
              loading={loading}
              fontSize="md"
            >
              {isReview ? 'Accept' : 'Next'}
            </Button>
          </div>
        </div>
      </form>
    );
  };

  return (
    <Modal
      visible={true}
      onCancel={() => {}}
      title={isReview ? 'Signature Details' : 'Add Signature'}
      wrapClassName={classes.modalWrapper}
      width={isReview ? 656 : 556}
    >
      <div className={classes.modalContent}>
        <Formik
          initialValues={initialValues}
          validateOnMount
          validationSchema={getSchema()}
          enableReinitialize
          onSubmit={values => {
            if (!isReview) {
              setIsReview(true);
            } else {
              setLoading(true);
              onSubmit(
                fonts[index],
                values,
                () => {
                  setLoading(false);
                },
                () => {
                  setIsReview(false);
                }
              );
            }
          }}
          innerRef={formRef}
        >
          {renderEditForm}
        </Formik>
      </div>
    </Modal>
  );
};

export default SignatureModal;
